import { useQuery } from '@tanstack/vue-query';
import { useUserStore } from '@store/user';
import type { MaybeRefOrGetter } from 'vue';
import companyDiaryApi, { type CompanyDiaryEntryType } from '@/api/companyDiary';

const STALE_TIME = 300_000; // 5 minutes

/**
 * The cached option lists the Company Diary's filters and pickers need: entry
 * types, and the tenant's people.
 *
 * Both are small, tenant-scoped and re-read on every modal open, so they are cached
 * through vue-query rather than fetched per component. **Every key is partitioned
 * by tenant** - without that, switching tenant serves the previous one's list from
 * cache until the stale time expires, which on a picker means assigning somebody
 * else's staff.
 *
 * Resources are deliberately absent: the entry modal picks them through the
 * diary's availability endpoint, where the answer depends on the window and so
 * cannot come from a cached roster.
 */

export interface CompanyDiaryOption {
    id: number;
    name: string;
}

/**
 * Entry types for the picker: active only, matching what the API will accept.
 * `StoreCompanyDiaryEntryRequest` refuses an inactive type, so offering one would
 * be a validation error dressed up as a choice.
 */
export function useCompanyDiaryEntryTypes(options: { includeInactive?: boolean } = {}) {
    const userStore = useUserStore();
    const includeInactive = options.includeInactive ?? false;

    const { data, isLoading, isFetching, isError, refetch } = useQuery<CompanyDiaryEntryType[]>({
        queryKey: computed(() => ['company-diary-entry-types', includeInactive, userStore.tenantId]),
        queryFn: async () => {
            const { data: response } = await companyDiaryApi.indexTypes(includeInactive ? { include_inactive: 1 } : {});

            return response.data ?? [];
        },
        staleTime: STALE_TIME,
    });

    return {
        entryTypes: computed<CompanyDiaryEntryType[]>(() => data.value ?? []),
        entryTypesLoading: isLoading,
        entryTypesRefreshing: isFetching,
        entryTypesError: isError,
        refreshEntryTypes: refetch,
    };
}

/**
 * The tenant's people as a flat `{ id, name }` option list, for the reminder
 * recipient picker.
 *
 * Deliberately the whole team rather than field staff alone: a reminder about a
 * truck's service is aimed at whoever needs to know, and the field-staff endpoint
 * the operational assign modal uses would silently hide half of them.
 *
 * There is no sibling list for vehicles, containers or trailers. Those are chosen
 * in the entry modal's resource accordions, which read the diary's availability
 * endpoint instead - a picker whose job is "who is free" cannot be served by a
 * cached roster (see `CompanyDiaryResourcePanel.vue`).
 */
export function useCompanyDiaryStaffOptions({ enabled = true }: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    const userStore = useUserStore();

    const staff = useQuery<CompanyDiaryOption[]>({
        queryKey: computed(() => ['company-diary-staff-options', userStore.tenantId]),
        queryFn: async () => {
            const { data } = await window.tenantAxios.get('/staff', { params: { limit: 500 } });

            return (data.data ?? []).map((user: any) => ({
                id: user.id,
                name: user.fullname || user.name || user.email,
            }));
        },
        staleTime: STALE_TIME,
        enabled: computed(() => toValue(enabled)),
    });

    return {
        staffOptions: computed<CompanyDiaryOption[]>(() => staff.data.value ?? []),
        staffOptionsLoading: staff.isLoading,
    };
}
